import { ChevronLeft, Menu, Pencil, Search } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import type { SessionStatus } from "../auth";
import { useShell, useShellSnapshot, useShellStorage } from "../core/shell";
import type { ShellCommand, ThreadGroup } from "../core/shell-types";
import { AppHeader, type ChromeProps } from "./AppChrome";
import type { ComposerSeed } from "./Composer";
import { Composer } from "./Composer";
import { MailboxList } from "./MailboxList";
import { MessageView, seedReply } from "./MessageView";
import { derivePane } from "./navigation";
import { ThreadList } from "./ThreadList";
import { useMailHistory } from "./useMailHistory";

const HEADER_BUTTON =
  "flex min-h-11 min-w-11 items-center justify-center gap-1 rounded-full px-2 text-sm text-foreground/80 transition-colors hover:bg-accent hover:text-foreground";

// The mail surface. Desktop (`md` up): three panes side by side under the
// shared header. Compact: one pane at a time — mailboxes (a full-screen
// drawer), the thread list, or the open message — derived from the snapshot
// (a selected thread means the message is showing) plus the drawer flag; the
// header shrinks to Back / title / actions and each stacked pane maps to a
// history entry so the platform back gesture pops it (useMailHistory).
export function MailShell({
  chrome,
  active,
  session,
  username,
  banners,
  composeRequested,
  onComposeHandled,
  messageRequested,
  onMessageHandled,
}: {
  chrome: ChromeProps;
  // False while another surface is showing: the DOM stays (an open composer
  // keeps its draft) but the shortcuts, the focus refresh and the history
  // layers are suspended.
  active: boolean;
  session: SessionStatus;
  username: string;
  banners: ReactNode;
  // A `?compose=1` launch or a lately-navigate {compose:true} request.
  composeRequested: boolean;
  onComposeHandled: () => void;
  // A notification click (or lately-navigate {openMessage:true}): the thread
  // selection follows in the snapshot; on compact the message pane must open.
  messageRequested: boolean;
  onMessageHandled: () => void;
}) {
  const shell = useShell();
  const snapshot = useShellSnapshot(shell);
  const storage = useShellStorage(shell);
  const { compact } = chrome;
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Compact only: the user opened the selected thread (tap, ⏎, notification);
  // the core keeps a thread selected whenever it can, so this is what tells
  // reading from browsing (navigation.ts derivePane).
  const [messageOpen, setMessageOpen] = useState(false);
  const [composerSeed, setComposerSeed] = useState<ComposerSeed | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const evicted = storage?.reason === "evicted";

  // Every mutation is a command to the core; the result shows up in the next
  // snapshot, so nothing here mirrors mail state. An evicted tab has no core
  // any more — the storage banner says so, and commands are dropped rather
  // than reported as errors.
  const run = useCallback(
    (command: ShellCommand) => {
      if (evicted) {
        return;
      }
      setLocalError(null);
      shell.command(command).catch((cause) => {
        setLocalError(cause instanceof Error ? cause.message : String(cause));
      });
    },
    [shell, evicted],
  );

  const selectedMailboxId = snapshot.selectedMailboxId ?? null;
  const selectedMailbox = useMemo(
    () => snapshot.mailboxes.find((mailbox) => mailbox.id === selectedMailboxId) ?? null,
    [snapshot.mailboxes, selectedMailboxId],
  );
  const selectedThreadId = snapshot.selectedThreadId ?? null;

  // Client-side filter over the loaded page (the shell has no search op yet).
  const query = searchQuery.trim().toLowerCase();
  const threads = useMemo(() => {
    if (!query) {
      return snapshot.threadGroups;
    }
    return snapshot.threadGroups.filter((thread) => {
      const latest = thread.latestEmail;
      const haystack = [
        latest.subject ?? "",
        latest.preview ?? "",
        ...thread.participantNames,
        ...(latest.from ?? []).map((address) => `${address.name ?? ""} ${address.email}`),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [snapshot.threadGroups, query]);

  const selectedThread = useMemo(
    () => snapshot.threadGroups.find((thread) => thread.threadId === selectedThreadId) ?? null,
    [snapshot.threadGroups, selectedThreadId],
  );

  // Keep the keyboard cursor inside the list as it changes shape.
  useEffect(() => {
    setFocusedIndex((current) => Math.min(current, Math.max(0, threads.length - 1)));
  }, [threads.length]);

  // The core polls the server itself; a refocus just nudges it.
  useEffect(() => {
    if (!active) {
      return;
    }
    const onFocus = () => run({ op: "refresh" });
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [run, active]);

  // Launch intent / navigation request: open a blank composer once.
  useEffect(() => {
    if (composeRequested && active) {
      setComposerSeed((current) => current ?? {});
      onComposeHandled();
    }
  }, [composeRequested, active, onComposeHandled]);
  useEffect(() => {
    if (messageRequested && active) {
      setMessageOpen(true);
      setDrawerOpen(false);
      onMessageHandled();
    }
  }, [messageRequested, active, onMessageHandled]);

  const openThread = useCallback(
    (thread: ThreadGroup) => {
      run({ op: "selectThread", threadId: thread.threadId });
      if (thread.hasUnread) {
        run({ op: "markThreadRead", threadId: thread.threadId, read: true });
      }
      setMessageOpen(true);
    },
    [run],
  );
  // Compact Back: the list again. The selection itself is left alone — the
  // core would re-select the first thread anyway, and keeping it means the
  // hydrated thread is still there when it is opened again.
  const closeThread = useCallback(() => setMessageOpen(false), []);
  const archive = useCallback(
    (thread: ThreadGroup) => run({ op: "archiveThread", threadId: thread.threadId }),
    [run],
  );
  const trash = useCallback(
    (thread: ThreadGroup) => run({ op: "deleteThread", threadId: thread.threadId }),
    [run],
  );
  const junk = useCallback(
    (thread: ThreadGroup) => run({ op: "junkThread", threadId: thread.threadId }),
    [run],
  );
  const toggleRead = useCallback(
    (thread: ThreadGroup) =>
      run({ op: "markThreadRead", threadId: thread.threadId, read: thread.hasUnread }),
    [run],
  );
  const toggleStar = useCallback(
    (thread: ThreadGroup) => run({ op: "toggleThreadStar", threadId: thread.threadId }),
    [run],
  );

  // Compact navigation: which pane is up, and the browser-history mirror of
  // the stack (only while this surface is the active one).
  const pane = derivePane({ compact, selectedThreadId, messageOpen, drawerOpen });
  useMailHistory({
    compact: compact && active,
    drawerOpen,
    messageOpen: messageOpen && selectedThreadId !== null,
    composerOpen: composerSeed !== null,
    onPop: ({ composerOpen, drawerOpen: drawerStays, messageOpen: messageStays }) => {
      if (!composerOpen) {
        setComposerSeed(null);
      }
      if (!drawerStays) {
        setDrawerOpen(false);
      }
      if (!messageStays) {
        setMessageOpen(false);
      }
    },
  });
  // Leaving the compact layout (rotation, window resize) drops the stack.
  useEffect(() => {
    if (!compact) {
      setDrawerOpen(false);
      setSearchOpen(false);
      setMessageOpen(false);
    }
  }, [compact]);
  useEffect(() => {
    if (searchOpen) {
      searchRef.current?.focus();
    }
  }, [searchOpen]);

  // Gmail-style keys: j/k move, ⏎ opens, e archives, # trashes, u toggles
  // unread, s stars, ! junks, r replies, c composes, / focuses search.
  // Mail-surface only: skipped while the calendar is up or the composer open.
  useEffect(() => {
    if (!active) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (composerSeed || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
      ) {
        return;
      }
      const focused = threads[focusedIndex];
      switch (event.key) {
        case "j":
        case "ArrowDown":
          event.preventDefault();
          setFocusedIndex((current) => Math.min(current + 1, Math.max(0, threads.length - 1)));
          break;
        case "k":
        case "ArrowUp":
          event.preventDefault();
          setFocusedIndex((current) => Math.max(current - 1, 0));
          break;
        case "Enter":
        case "o":
          if (focused) {
            event.preventDefault();
            openThread(focused);
          }
          break;
        case "e":
          if (focused) {
            event.preventDefault();
            archive(focused);
          }
          break;
        case "#":
          if (focused) {
            event.preventDefault();
            trash(focused);
          }
          break;
        case "!":
          if (focused) {
            event.preventDefault();
            junk(focused);
          }
          break;
        case "u":
          if (focused) {
            event.preventDefault();
            toggleRead(focused);
          }
          break;
        case "s":
          if (focused) {
            event.preventDefault();
            toggleStar(focused);
          }
          break;
        case "r":
          if (selectedThread) {
            event.preventDefault();
            const newest = [...snapshot.selectedThreadEmails].sort((a, b) =>
              b.receivedAt.localeCompare(a.receivedAt),
            )[0];
            if (newest) {
              setComposerSeed(seedReply(newest));
            }
          }
          break;
        case "c":
          event.preventDefault();
          setComposerSeed({});
          break;
        case "/":
          event.preventDefault();
          setSearchOpen(true);
          searchRef.current?.focus();
          break;
        case "Escape":
          if (searchQuery) {
            setSearchQuery("");
          } else if (compact && messageOpen) {
            closeThread();
          }
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    active,
    compact,
    composerSeed,
    threads,
    focusedIndex,
    openThread,
    closeThread,
    archive,
    trash,
    junk,
    toggleRead,
    toggleStar,
    selectedThread,
    messageOpen,
    snapshot.selectedThreadEmails,
    searchQuery,
  ]);

  const live = shell.hasSnapshot;

  // Once the core has reported an error through the snapshot it owns error
  // reporting (it clears `error` when a background reconnect succeeds); the
  // open() rejection is only the fallback before that.
  const [coreReportedError, setCoreReportedError] = useState(false);
  useEffect(() => {
    if (snapshot.error) {
      setCoreReportedError(true);
    }
  }, [snapshot.error]);
  // The latch is per session: a new open (account switch, retry) starts
  // over, otherwise a single past error would hide open() failures forever.
  useEffect(() => {
    if (session.status === "opening") {
      setCoreReportedError(false);
    }
  }, [session.status]);
  const bannerError =
    localError ??
    snapshot.error ??
    (session.status === "failed" && !coreReportedError ? session.error : null);
  // The initial connect failed and the core keeps retrying with backoff
  // (snapshot.error carries the latest attempt's error until one succeeds);
  // a manual refresh triggers an attempt right away.
  const retrying =
    !localError &&
    session.status !== "open" &&
    (Boolean(snapshot.error) || session.status === "failed");

  const listTitle = query
    ? `Matching “${searchQuery.trim()}”`
    : (selectedMailbox?.name ?? (snapshot.mailboxes.length === 0 ? "" : "Mail"));

  const searchInput = (
    <input
      ref={searchRef}
      type="search"
      value={searchQuery}
      onChange={(event) => setSearchQuery(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.currentTarget.blur();
          if (compact) {
            setSearchQuery("");
            setSearchOpen(false);
          }
        } else if (event.key === "Enter") {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
      placeholder={compact ? "Search mail" : "Search mail ( / )"}
      aria-label="Search mail"
      className="w-full rounded-full border border-input bg-muted/40 py-1.5 pl-9 pr-4 text-sm outline-none transition-colors focus:border-ring focus:bg-background focus:ring-2 focus:ring-ring/30"
    />
  );

  // The header's slots per layout / pane.
  let header: ReactNode;
  if (!compact) {
    header = (
      <AppHeader
        chrome={chrome}
        center={
          <div className="relative mx-auto max-w-md">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            {searchInput}
          </div>
        }
        right={
          <button
            type="button"
            onClick={() => setComposerSeed({})}
            className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <Pencil aria-hidden className="h-3.5 w-3.5" />
            Compose
          </button>
        }
      />
    );
  } else if (pane === "message") {
    header = (
      <AppHeader
        chrome={chrome}
        left={
          <button
            type="button"
            onClick={closeThread}
            data-back
            className={cn(HEADER_BUTTON, "-ml-1 pr-3")}
          >
            <ChevronLeft aria-hidden className="h-5 w-5" />
            <span className="max-w-32 truncate">{selectedMailbox?.name ?? "Back"}</span>
          </button>
        }
      />
    );
  } else if (pane === "mailboxes") {
    header = (
      <AppHeader
        chrome={chrome}
        left={
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            data-back
            className={cn(HEADER_BUTTON, "-ml-1 pr-3")}
          >
            <ChevronLeft aria-hidden className="h-5 w-5" />
            Back
          </button>
        }
        title="Mailboxes"
      />
    );
  } else {
    header = (
      <AppHeader
        chrome={chrome}
        left={
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Mailboxes"
            data-mailboxes
            className={cn(HEADER_BUTTON, "-ml-1")}
          >
            <Menu aria-hidden className="h-5 w-5" />
          </button>
        }
        title={listTitle}
        right={
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search mail"
            className={HEADER_BUTTON}
          >
            <Search aria-hidden className="h-5 w-5" />
          </button>
        }
        expanded={
          searchOpen ? (
            <div className="flex w-full items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search
                  aria-hidden
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                />
                {searchInput}
              </div>
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setSearchOpen(false);
                }}
                className={HEADER_BUTTON}
              >
                Cancel
              </button>
            </div>
          ) : undefined
        }
      />
    );
  }

  return (
    <div
      data-mail-pane={pane}
      className={cn(
        "min-h-0 flex-1 flex-col bg-background text-foreground",
        active ? "flex" : "hidden",
      )}
    >
      {header}

      {banners}

      {bannerError ? (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive"
        >
          <span className="min-w-0 flex-1 basis-48 break-words">{bannerError}</span>
          {retrying ? (
            <button
              type="button"
              onClick={() => run({ op: "refresh" })}
              title="Lately keeps retrying in the background; click to try now"
              className="shrink-0 rounded px-1.5 text-xs text-destructive/80 hover:bg-destructive/10"
            >
              Retrying…
            </button>
          ) : null}
          {localError ? (
            <button
              type="button"
              onClick={() => setLocalError(null)}
              className="shrink-0 rounded px-1.5 text-xs hover:bg-destructive/10"
            >
              Dismiss
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="relative flex min-h-0 min-w-0 flex-1 overflow-x-hidden">
        {pane === "split" || pane === "mailboxes" ? (
          <MailboxList
            compact={compact}
            mailboxes={snapshot.mailboxes}
            ownerTitle={username}
            selectedMailboxId={selectedMailboxId}
            onSelect={(mailboxId) => {
              setSearchQuery("");
              setFocusedIndex(0);
              setDrawerOpen(false);
              run({ op: "selectMailbox", mailboxId });
            }}
          />
        ) : null}
        <ThreadList
          compact={compact}
          hidden={pane === "mailboxes" || pane === "message"}
          threads={threads}
          total={query ? threads.length : snapshot.totalEmailCount}
          loading={snapshot.isRefreshing || (session.status === "opening" && !live)}
          title={listTitle}
          selectedThreadId={compact && !messageOpen ? null : selectedThreadId}
          focusedIndex={focusedIndex}
          canLoadMore={!query && snapshot.canLoadMore}
          onOpen={openThread}
          onLoadMore={() => run({ op: "loadMore" })}
        />
        {pane === "split" || pane === "message" ? (
          <MessageView
            compact={compact}
            snapshot={snapshot}
            thread={selectedThread}
            onArchive={archive}
            onTrash={trash}
            onJunk={junk}
            onToggleRead={toggleRead}
            onToggleStar={toggleStar}
            onReply={(seed) => setComposerSeed(seed)}
          />
        ) : null}
        {pane === "threads" ? (
          <button
            type="button"
            onClick={() => setComposerSeed({})}
            aria-label="Compose"
            data-compose-fab
            className="absolute bottom-4 right-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-colors hover:bg-primary/90"
          >
            <Pencil aria-hidden className="h-5 w-5" />
          </button>
        ) : null}
      </div>

      {composerSeed ? (
        <Composer
          shell={shell}
          compact={compact}
          seed={composerSeed}
          onClose={() => setComposerSeed(null)}
          onSent={() => setComposerSeed(null)}
        />
      ) : null}
    </div>
  );
}
